import {
  ChangeDetectorRef,
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { CustomerService } from '../../services/customer-service/customer-service';
import {
  isValidCompanyName,
  isValidEmail,
  isValidName,
  sanitizeCompanyNameInput,
  sanitizeNameInput
} from '../../shared/helpers';

@Component({
  selector: 'app-gap-check-intro',
  standalone: true,
  imports: [FormsModule, CommonModule, RouterLink],
  templateUrl: './gap-check-intro.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./gap-check-intro.css'],
})
export class GapCheckIntro implements OnInit {
  private router = inject(Router);
  private customerService = inject(CustomerService);
  private cdr = inject(ChangeDetectorRef);

  // ISO Selection
  selectedIso: string | null = null;
  showIsoModal = false;
  showIsoError = false;

  customer = {
    companyName: '',
    name: '',
    contactNumber: '',
    email: '',
  };

  previousEmail = '';
  previousName = '';
  previousCompany = '';
  previousMobile = '';

  errors: Record<string, string> = {};

  ngOnInit() {
    console.log(this.errors);
  }

  // =========================
  // ISO MODAL METHODS
  // =========================

  openIsoModal() {
    this.showIsoModal = true;
    this.showIsoError = false;
  }

  closeIsoModal(event?: Event) {
    if (event && event.target !== event.currentTarget) {
      return; // Only close if clicking backdrop
    }
    this.showIsoModal = false;
  }

  selectIso(standard: string) {
    this.selectedIso = standard;
    this.showIsoError = false;
    this.cdr.detectChanges();
  }

  confirmIsoSelection() {
    if (this.selectedIso) {
      this.showIsoModal = false;
      this.showIsoError = false;
      this.cdr.detectChanges();
    }
  }

  // =========================
  // FIXED INPUT HANDLERS
  // =========================

  onMobileInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    value = value.replace(/[^\d+\-\s]/g, '');
    value = value.replace(/\+(?=.+\+)/g, '');

    if (value.includes('+') && !value.startsWith('+')) {
      value = '+' + value.replace(/\+/g, '');
    }

    value = value.substring(0, 30);

    this.customer.contactNumber = value;
    input.value = value;
    this.previousMobile = value;

    const digitCount = (value.match(/\d/g) || []).length;

    const validFormat = /^\+?[0-9\s-]+$/;

    if (value.trim() === '') {
      this.errors['contactNumber'] = 'Contact Number is required.';
      return;
    }

    if (!validFormat.test(value)) {
      this.errors['contactNumber'] = 'Please enter a valid contact number.';
      return;
    }

    if (digitCount < 7 || digitCount > 15) {
      this.errors['contactNumber'] = 'Contact Number must be 7–15 digits.';
      return;
    }

    delete this.errors['contactNumber'];
  }

  onEmailInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value === '' || /^[^\s@]*@?[^\s@]*\.?[^\s@]*$/.test(value)) {
      this.customer.email = value;
      this.previousEmail = value;
    } else {
      input.value = this.previousEmail;
      this.customer.email = this.previousEmail;
    }

    if (value !== '' && !isValidEmail(this.customer.email)) {
      this.errors['email'] = 'Please enter a valid email.';
    } else {
      delete this.errors['email'];
    }

    this.cdr.detectChanges();
  }

  onNameInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    const sanitized = sanitizeNameInput(value, this.previousName, 50);

    input.value = sanitized;
    this.customer.name = sanitized;

    if (sanitized !== this.previousName) {
      this.previousName = sanitized;
    }

    if (value !== '' && !isValidName(this.customer.name)) {
      this.errors['name'] =
        'Please enter a valid name (letters, spaces, hyphens allowed).';
    } else {
      delete this.errors['name'];
    }
  }

  onCompanyInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    const sanitized = sanitizeCompanyNameInput(
      value,
      this.previousCompany,
      100
    );

    input.value = sanitized;
    this.customer.companyName = sanitized;

    if (sanitized !== this.previousCompany) {
      this.previousCompany = sanitized;
    }

    if (value !== '' && !isValidCompanyName(this.customer.companyName)) {
      this.errors['companyName'] =
        'Please enter a valid company name (letters, numbers, & symbols allowed).';
    } else {
      delete this.errors['companyName'];
    }
  }

  // =========================
  // VALIDATION
  // =========================

  get hasErrors(): boolean {
    return Object.keys(this.errors).length > 0;
  }

  beginAssessment() {
    this.errors = {};
    this.showIsoError = false;

    // Validate ISO selection
    if (!this.selectedIso) {
      this.showIsoError = true;
      this.openIsoModal();
      return;
    }

    if (!this.customer.companyName.trim()) {
      this.errors['companyName'] = 'Company Name is required.';
    }

    if (!this.customer.name.trim()) {
      this.errors['name'] = 'Representative Name is required.';
    }

    if (!this.customer.contactNumber?.trim()) {
      this.errors['contactNumber'] = 'Contact Number is required.';
    }

    if (!this.customer.email.trim()) {
      this.errors['email'] = 'Email is required.';
    } else if (!isValidEmail(this.customer.email)) {
      this.errors['email'] = 'Email format is invalid.';
    }

    if (Object.keys(this.errors).length > 0) return;

    this.customerService.setCustomer(this.customer);

    // Navigate with selected standard
    this.router.navigate(['/gap-check'], { 
      queryParams: { standard: this.selectedIso }
    });
  }
}